// «Editor del sitio» — vista previa GRANDE del sitio público con los cambios sin
// publicar + panel lateral (Editar / Agente) + barra de cambios pendientes.
//
// Todo cambio (a mano o del agente) es una revisión de mcp_django
// (apps/site_config): según la política de la org queda pendiente (la persona
// admin la publica aquí) o se aplica directo; siempre queda en el historial y se
// puede deshacer. La vista previa es el sitio real de la org con
// `?__preview=<token>` (token firmado de 30 min; ver api.ts) y habla con este
// panel por postMessage solo con el origen del sitio.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bot,
  Braces,
  Check,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Eye,
  EyeOff,
  GitPullRequestArrow,
  Globe,
  Loader2,
  MessageSquarePlus,
  Monitor,
  Moon,
  PanelRightClose,
  PanelRightOpen,
  Pencil,
  RotateCcw,
  Smartphone,
  Sun,
  SunMoon,
  Tablet,
  User,
  X,
} from 'lucide-react';

import type { Config, PendingRevision, SectionState, SiteEditorApi, SectionPatch, EditResult } from './api';
import { liveApi } from './api';
import { clone, equal, sectionLabel, setOps } from './fields';
import { FieldsForm } from './FieldsForm';
import { Button, EmptyState, ErrorNote } from '@/modules/orion-shell/ui';

type Device = 'desktop' | 'tablet' | 'phone';
type SiteTheme = 'auto' | 'light' | 'dark';
type PanelTab = 'editar' | 'agente';
type MobileView = 'preview' | PanelTab;

const DEVICES: Record<Device, { w: number; h: number; label: string; icon: ReactNode }> = {
  desktop: { w: 1280, h: 0, label: 'Escritorio', icon: <Monitor size={16} aria-hidden /> },
  tablet: { w: 820, h: 1180, label: 'Tableta', icon: <Tablet size={16} aria-hidden /> },
  phone: { w: 390, h: 844, label: 'Celular', icon: <Smartphone size={16} aria-hidden /> },
};
const NS = 'orion-site-preview:';
const TOKEN_REFRESH_MS = 25 * 60 * 1000;
const REASON = 'Editado desde el editor visual del sitio';

export interface SiteEditorProps {
  api?: SiteEditorApi;
  isAdmin: boolean;
  /** Origen que se firma en el token (el de esta pestaña). */
  parentOrigin?: string;
  /** Chat del agente (el real: ChatPane acotado a site_config). */
  renderAgent?: (ctx: { page: string; pageName: string; selected: string | null; onTurnDone: () => void; chatKey: number }) => ReactNode;
  /** Alto del editor (shell: bajo la barra superior). */
  height?: string;
  /** Botón principal con el degradado del shell (solo dentro de .orion-shell). */
  brandButton?: boolean;
  /** Estado inicial (página de desarrollo / enlaces profundos). */
  initial?: { selected?: string; tab?: PanelTab; mobileView?: MobileView; device?: Device; siteTheme?: SiteTheme };
}

function pageName(slug: string) {
  return slug ? `/${slug}` : 'Inicio';
}

function useIsMobile() {
  const q = '(max-width: 1023px)';
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return m;
}

function Seg<T extends string>({ value, onChange, items, label }: { value: T; onChange: (v: T) => void; items: Array<{ v: T; label: string; icon?: ReactNode; text?: boolean }>; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex shrink-0 items-center gap-0.5 rounded-xl border border-border bg-card p-0.5">
      {items.map((it) => {
        const on = it.v === value;
        return (
          <button
            key={it.v}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={it.text ? undefined : it.label}
            title={it.label}
            onClick={() => onChange(it.v)}
            className={`inline-flex h-8 items-center justify-center gap-1.5 rounded-lg px-2.5 text-sm font-medium transition-colors ${on ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
          >
            {it.icon}
            {it.text && <span>{it.label}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function SiteEditor({ api = liveApi, isAdmin, parentOrigin, renderAgent, height = 'calc(100dvh - 64px)', brandButton = true, initial }: SiteEditorProps) {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const origin = parentOrigin ?? (typeof window !== 'undefined' ? window.location.origin : '');

  const [page, setPage] = useState('');
  const [device, setDevice] = useState<Device>(
    initial?.device ?? (typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches ? 'phone' : 'desktop'),
  );
  const [siteTheme, setSiteTheme] = useState<SiteTheme>(initial?.siteTheme ?? 'auto');
  const [panelOpen, setPanelOpen] = useState(true);
  const [tab, setTab] = useState<PanelTab>(initial?.tab ?? 'editar');
  const [mobileView, setMobileView] = useState<MobileView>(initial?.mobileView ?? 'preview');
  const [selected, setSelected] = useState<string | null>(initial?.selected ?? null);
  const [frameKey, setFrameKey] = useState(0);
  const [frameReady, setFrameReady] = useState(false);
  // Cada «ready» del puente del sitio (tras hidratar) → reenviar selección y tema.
  const [bridgeTick, setBridgeTick] = useState(0);
  const [chatKey, setChatKey] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [recent, setRecent] = useState<Array<{ id: number; summary: string }>>([]);

  // ── Datos ──────────────────────────────────────────────
  const pages = useQuery({ queryKey: ['site-editor', 'pages'], queryFn: () => api.pages() });
  const token = useQuery({
    queryKey: ['site-editor', 'token', page, origin],
    queryFn: () => api.previewToken(page, origin),
    staleTime: TOKEN_REFRESH_MS,
    refetchInterval: TOKEN_REFRESH_MS,
  });
  const draft = useQuery({
    queryKey: ['site-editor', 'draft', page, token.data?.token],
    queryFn: () => api.draft(token.data!.token, page),
    enabled: !!token.data?.token,
  });
  const pending = useQuery({
    queryKey: ['site-editor', 'pending', page],
    queryFn: () => api.pending(page),
    refetchInterval: tab === 'agente' || mobileView === 'agente' ? 6000 : 30000,
  });
  const editMode = useQuery({ queryKey: ['site-editor', 'mode'], queryFn: () => api.editMode() });

  const sections = draft.data ?? [];
  const pendingList = useMemo(() => [...(pending.data ?? [])].sort((a, b) => a.id - b.id), [pending.data]);
  const pendingByKey = useMemo(() => {
    const m = new Map<string, PendingRevision[]>();
    for (const r of pendingList) m.set(r.section_key, [...(m.get(r.section_key) ?? []), r]);
    return m;
  }, [pendingList]);
  const current = sections.find((s) => s.section_key === selected) ?? null;

  const reloadPreview = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['site-editor', 'draft', page] });
    setFrameReady(false);
    setFrameKey((k) => k + 1);
  }, [qc, page]);

  const refreshAll = useCallback(async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['site-editor', 'pending', page] }),
      qc.invalidateQueries({ queryKey: ['site-editor', 'published', page] }),
      qc.invalidateQueries({ queryKey: ['site-editor', 'pages'] }),
      qc.invalidateQueries({ queryKey: ['orion-shell', 'revisions'] }),
    ]);
    reloadPreview();
  }, [qc, page, reloadPreview]);

  // Una propuesta nueva (p. ej. del agente) recarga la vista previa al instante.
  const pendingSig = pendingList.map((r) => r.id).join(',');
  const lastSig = useRef<string | null>(null);
  useEffect(() => {
    if (pending.data === undefined) return;
    if (lastSig.current !== null && lastSig.current !== pendingSig) reloadPreview();
    lastSig.current = pendingSig;
  }, [pendingSig, pending.data, reloadPreview]);

  const lastPage = useRef(page);
  useEffect(() => {
    if (lastPage.current === page) return; // (también evita el doble efecto de StrictMode)
    lastPage.current = page;
    setSelected(null);
    lastSig.current = null;
    setFrameReady(false);
  }, [page]);

  // ── Formulario de la sección elegida ───────────────────
  const [form, setForm] = useState<Config | null>(null);
  const [jsonOpen, setJsonOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonErr, setJsonErr] = useState<string | null>(null);
  const dirty = !!(current && form && !equal(form, current.config));
  const formFor = useRef<string | null>(null);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  useEffect(() => {
    // Al cambiar de sección (o al llegar una versión nueva, p. ej. del agente) se toma
    // el contenido con borradores — salvo que la persona esté a media edición.
    if (formFor.current === selected && dirtyRef.current) return;
    formFor.current = selected;
    setForm(current ? clone(current.config) : null);
    setJsonErr(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, current && JSON.stringify(current.config)]);
  useEffect(() => {
    if (form) setJsonText(JSON.stringify(form, null, 2));
  }, [form]);

  // ── Vista previa (iframe + mensajes) ───────────────────
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const previewUrl = token.data?.url || '';
  const previewOrigin = useMemo(() => {
    try {
      return previewUrl ? new URL(previewUrl, window.location.href).origin : '';
    } catch {
      return '';
    }
  }, [previewUrl]);
  const post = useCallback(
    (msg: Record<string, unknown>) => {
      const w = frameRef.current?.contentWindow;
      if (w && previewOrigin) w.postMessage(msg, previewOrigin);
    },
    [previewOrigin],
  );

  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (!frameRef.current || e.source !== frameRef.current.contentWindow || e.origin !== previewOrigin) return;
      const d = e.data as { type?: string; key?: string } | null;
      if (!d || typeof d.type !== 'string') return;
      if (d.type === `${NS}ready`) {
        setFrameReady(true);
        setBridgeTick((t) => t + 1);
      }
      if (d.type === `${NS}select` && typeof d.key === 'string') {
        setSelected(d.key);
        setTab('editar');
        setPanelOpen(true);
        if (isMobile) setMobileView('editar');
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [previewOrigin, isMobile]);

  useEffect(() => {
    if (!bridgeTick) return;
    if (siteTheme !== 'auto') post({ type: `${NS}theme`, mode: siteTheme });
    if (selected) post({ type: `${NS}select`, key: selected });
  }, [bridgeTick, siteTheme, selected, post]);

  // Escalado del dispositivo dentro del escenario.
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [stage, setStage] = useState({ w: 1000, h: 700 });
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setStage({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setStage({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, [isMobile, mobileView, panelOpen]);
  const dev = DEVICES[device];
  const pad = isMobile ? 0 : 24;
  const availW = Math.max(stage.w - pad * 2, 200);
  const availH = Math.max(stage.h - pad * 2, 200);
  const scale = Math.min(1, availW / dev.w, dev.h ? availH / dev.h : 1);
  const frameH = dev.h ? dev.h : availH / scale;

  // ── Escrituras (todas crean revisiones) ────────────────
  const run = async (label: string, fn: () => Promise<string | void>) => {
    setBusy(label);
    setNotice(null);
    try {
      const msg = await fn();
      if (msg) setNotice({ tone: 'ok', text: msg });
    } catch (e: any) {
      setNotice({ tone: 'err', text: e?.message || 'No se pudo completar la acción.' });
    } finally {
      setBusy(null);
      await refreshAll();
    }
  };

  const resultText = (r: EditResult | null) =>
    !r || r.status === 'unchanged'
      ? 'Sin cambios: la sección ya estaba así.'
      : r.status === 'applied'
        ? 'Aplicado en tu sitio. Lo puedes deshacer abajo.'
        : isAdmin
          ? 'Quedó como cambio pendiente: revísalo en la vista previa y publícalo.'
          : 'Quedó como propuesta: una persona administradora la publica.';

  /** Lleva la sección al estado `desired` con UNA revisión desde lo publicado.
   *  Las propuestas pendientes anteriores de esa sección ya van incluidas (el
   *  formulario parte de la vista previa), así que la persona admin las
   *  descarta para que publicar no choque. */
  const commit = async (key: string, desired: SectionState): Promise<EditResult | null> => {
    const pub = (await qc.fetchQuery({ queryKey: ['site-editor', 'published', page], queryFn: () => api.published(page), staleTime: 0 }))
      .find((s) => s.section_key === key);
    if (!pub) throw new Error('Esta sección todavía es una propuesta (no está publicada). Publícala primero en la barra de cambios.');
    const older = pendingByKey.get(key) ?? [];
    const body: SectionPatch = { reason: older.length ? `${REASON} (reúne ${older.map((o) => `#${o.id}`).join(', ')})`.slice(0, 480) : REASON };
    const ops = setOps(pub.config, desired.config);
    if (Object.keys(ops).length) body.set = ops;
    if (desired.is_enabled !== pub.is_enabled) body.is_enabled = desired.is_enabled;
    if (desired.order !== pub.order) body.order = desired.order;
    const changes = body.set || body.is_enabled !== undefined || body.order !== undefined;
    const res = changes ? await api.patch(page, key, body) : null;
    if (isAdmin && older.length && (!res || res.status !== 'unchanged')) {
      for (const o of older) await api.reject(o.id).catch(() => undefined);
    }
    if (res?.status === 'applied' && res.revision_id) setRecent((r) => [{ id: res.revision_id!, summary: res.summary }, ...r].slice(0, 5));
    return res;
  };

  const saveForm = () =>
    current && form && run('save', async () => resultText(await commit(current.section_key, { ...current, config: form })));

  const toggle = (s: SectionState) =>
    run(`toggle-${s.section_key}`, async () => resultText(await commit(s.section_key, { ...s, is_enabled: !s.is_enabled })));

  const move = (idx: number, dir: -1 | 1) =>
    run(`move-${idx}`, async () => {
      const list = [...sections];
      const j = idx + dir;
      if (j < 0 || j >= list.length) return;
      [list[idx], list[j]] = [list[j], list[idx]];
      // Cada posición conserva su valor de `order` (así solo cambian las dos
      // secciones movidas); si hay empates, se renumera de 10 en 10.
      const vals = sections.map((s) => s.order);
      const distinct = vals.every((o, i) => i === 0 || o > vals[i - 1]);
      const orders = distinct ? vals : list.map((_, i) => (i + 1) * 10);
      let last: EditResult | null = null;
      for (let i = 0; i < list.length; i++) {
        if (list[i].order !== orders[i]) last = await commit(list[i].section_key, { ...list[i], order: orders[i] });
      }
      return resultText(last);
    });

  const approve = (r: PendingRevision) =>
    run(`approve-${r.id}`, async () => {
      const res = await api.approve(r.id);
      setRecent((x) => [{ id: r.id, summary: r.summary }, ...x].slice(0, 5));
      return res.note || 'Publicado.';
    });
  const approveAll = () =>
    run('approve-all', async () => {
      let n = 0;
      for (const r of pendingList) {
        await api.approve(r.id);
        setRecent((x) => [{ id: r.id, summary: r.summary }, ...x].slice(0, 5));
        n += 1;
      }
      return n === 1 ? 'Publicado 1 cambio.' : `Publicados ${n} cambios.`;
    });
  const reject = (r: PendingRevision) => run(`reject-${r.id}`, async () => (await api.reject(r.id)).note || 'Descartado; tu sitio no cambió.');
  const undo = (id: number) =>
    run(`undo-${id}`, async () => {
      const res = await api.revert(id, 'Deshecho desde el editor visual del sitio');
      setRecent((x) => x.filter((y) => y.id !== id));
      return res.status === 'applied' ? 'Deshecho: tu sitio volvió a como estaba.' : resultText(res);
    });

  const selectFromPanel = (key: string) => {
    setSelected(key);
    post({ type: `${NS}select`, key });
  };

  // ── Piezas ─────────────────────────────────────────────
  const pageOptions = pages.data?.length ? pages.data : [{ page_slug: '', sections: 0, enabled: 0 }];
  const primaryVariant = brandButton ? 'brand' : 'primary';

  const toolbar = (
    <div className="flex min-w-0 flex-wrap items-center gap-2 border-b border-border px-3 py-2.5 sm:px-4">
      <div className="mr-auto flex min-w-0 items-center gap-2">
        <span aria-hidden className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary sm:flex"><Globe size={18} /></span>
        <div className="min-w-0">
          <h1 className="truncate text-base font-semibold tracking-tight text-foreground sm:text-lg">Editor del sitio</h1>
          <p className="hidden truncate text-xs text-muted-foreground sm:block">
            {editMode.data === 'apply' ? 'Tus cambios se aplican directo y quedan en el historial.' : 'Tus cambios quedan pendientes hasta que los publiques.'}
          </p>
        </div>
      </div>
      <label className="sr-only" htmlFor="se-page">Página</label>
      <select
        id="se-page"
        value={page}
        onChange={(e) => setPage(e.target.value)}
        className="h-9 max-w-[180px] rounded-xl border border-border bg-card px-2.5 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
      >
        {pageOptions.map((p) => (
          <option key={p.page_slug} value={p.page_slug}>{pageName(p.page_slug)}</option>
        ))}
      </select>
      {(!isMobile || mobileView === 'preview') && (
        <>
          <Seg label="Dispositivo" value={device} onChange={setDevice} items={(Object.keys(DEVICES) as Device[]).map((d) => ({ v: d, label: DEVICES[d].label, icon: DEVICES[d].icon }))} />
          <Seg
            label="Tema del sitio"
            value={siteTheme}
            onChange={setSiteTheme}
            items={[
              { v: 'auto', label: 'Tema del sitio: automático', icon: <SunMoon size={16} aria-hidden /> },
              { v: 'light', label: 'Sitio en modo claro', icon: <Sun size={16} aria-hidden /> },
              { v: 'dark', label: 'Sitio en modo oscuro', icon: <Moon size={16} aria-hidden /> },
            ]}
          />
        </>
      )}
      {previewUrl && !isMobile && (
        <a href={previewUrl} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-border bg-card px-3 text-sm font-medium text-foreground hover:bg-muted">
          <ExternalLink size={14} aria-hidden /> Abrir vista previa<span className="sr-only"> (se abre en otra pestaña)</span>
        </a>
      )}
      {!isMobile && (
        <Button variant="secondary" onClick={() => setPanelOpen((o) => !o)} aria-label={panelOpen ? 'Ocultar panel' : 'Mostrar panel'} aria-expanded={panelOpen}>
          {panelOpen ? <PanelRightClose size={16} aria-hidden /> : <PanelRightOpen size={16} aria-hidden />}
        </Button>
      )}
    </div>
  );

  const pendingBar = (pendingList.length > 0 || recent.length > 0) && (
    <div className="border-b border-border bg-primary/[0.04] px-3 py-2 sm:px-4" aria-label="Cambios sin publicar" role="region">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <GitPullRequestArrow size={15} className="text-primary" aria-hidden />
          {pendingList.length ? `${pendingList.length} ${pendingList.length === 1 ? 'cambio sin publicar' : 'cambios sin publicar'}` : 'Publicado'}
        </span>
        <ul className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto py-0.5 [scrollbar-width:thin]">
          {pendingList.map((r) => (
            <li key={r.id} className="flex shrink-0 items-center gap-1 rounded-full border border-border bg-card py-0.5 pl-2.5 pr-1 text-xs">
              {r.actor_kind === 'human' ? <User size={12} aria-hidden className="text-muted-foreground" /> : <Bot size={12} aria-hidden className="text-primary" />}
              <button type="button" onClick={() => selectFromPanel(r.section_key)} className="max-w-[220px] truncate text-foreground hover:underline" title={r.summary}>
                {r.summary || sectionLabel(r.section_key)}
              </button>
              {isAdmin && (
                <>
                  <button type="button" disabled={!!busy} onClick={() => void approve(r)} aria-label={`Publicar: ${r.summary}`} className="rounded-full p-1 text-primary hover:bg-primary/10 disabled:opacity-50"><Check size={13} /></button>
                  <button type="button" disabled={!!busy} onClick={() => void reject(r)} aria-label={`Descartar: ${r.summary}`} className="rounded-full p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"><X size={13} /></button>
                </>
              )}
            </li>
          ))}
          {recent.map((r) => (
            <li key={`u-${r.id}`} className="flex shrink-0 items-center gap-1 rounded-full bg-muted py-0.5 pl-2.5 pr-1 text-xs text-muted-foreground">
              <Check size={12} aria-hidden /> <span className="max-w-[180px] truncate">{r.summary}</span>
              {isAdmin && (
                <button type="button" disabled={!!busy} onClick={() => void undo(r.id)} className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-medium text-foreground hover:bg-card disabled:opacity-50">
                  <RotateCcw size={12} aria-hidden /> Deshacer
                </button>
              )}
            </li>
          ))}
        </ul>
        {pendingList.length > 0 && (isAdmin ? (
          <div className="flex shrink-0 gap-1.5">
            <Button variant={primaryVariant} disabled={!!busy} onClick={() => void approveAll()}>
              {busy === 'approve-all' ? <Loader2 size={15} className="animate-spin" aria-hidden /> : <Check size={15} aria-hidden />} Publicar{pendingList.length > 1 ? ' todo' : ''}
            </Button>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">Solo una persona administradora publica.</span>
        ))}
      </div>
    </div>
  );

  const previewStage = (
    <div ref={stageRef} className={`relative flex min-h-0 min-w-0 flex-1 items-start justify-center overflow-hidden ${isMobile ? '' : 'rounded-[20px] border border-border bg-muted/60 p-6'}`}>
      {token.isError ? (
        <div className="m-auto max-w-sm"><EmptyState icon={<Globe size={18} />} title="No se pudo abrir la vista previa">Revisa que tu sitio tenga dominio activo e inténtalo de nuevo.</EmptyState></div>
      ) : token.isLoading ? (
        <div className="m-auto flex items-center gap-2 text-sm text-muted-foreground" role="status"><Loader2 size={16} className="animate-spin" aria-hidden /> Preparando la vista previa…</div>
      ) : !previewUrl ? (
        <div className="m-auto max-w-sm"><EmptyState icon={<Globe size={18} />} title="Tu sitio aún no tiene dominio">Cuando tu organización tenga un dominio activo verás aquí tu sitio con los cambios sin publicar.</EmptyState></div>
      ) : (
        <div
          className={`relative shrink-0 overflow-hidden bg-card shadow-[0_20px_60px_-20px_rgba(67,56,202,0.35)] ${isMobile ? '' : device === 'desktop' ? 'rounded-2xl border border-border' : 'rounded-[28px] border-[6px] border-foreground/80'}`}
          style={{ width: dev.w * scale, height: frameH * scale }}
        >
          <iframe
            key={frameKey}
            ref={frameRef}
            src={previewUrl}
            title={`Vista previa de ${pageName(page)} con cambios sin publicar`}
            className="absolute left-0 top-0 origin-top-left border-0 bg-white"
            style={{ width: dev.w, height: frameH, transform: `scale(${scale})` }}
            onLoad={() => window.setTimeout(() => setFrameReady(true), 50)}
          />
          {!frameReady && (
            <div className="pointer-events-none absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-card/90 px-2.5 py-1 text-xs text-muted-foreground shadow"><Loader2 size={12} className="animate-spin" aria-hidden /> Actualizando…</div>
          )}
        </div>
      )}
    </div>
  );

  const sectionsList = (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">Secciones de {pageName(page)}</h2>
        <span className="text-xs text-muted-foreground">{sections.filter((s) => s.is_enabled).length}/{sections.length} visibles</span>
      </div>
      {draft.isLoading ? (
        <p className="py-3 text-sm text-muted-foreground" role="status">Cargando…</p>
      ) : sections.length === 0 ? (
        <EmptyState title="Esta página no tiene secciones">Pídele al agente que agregue una (pestaña Agente).</EmptyState>
      ) : (
        <ol className="flex flex-col gap-1">
          {sections.map((s, i) => {
            const on = s.section_key === selected;
            const pend = pendingByKey.get(s.section_key)?.length ?? 0;
            return (
              <li key={s.section_key} className={`group flex items-center gap-1 rounded-xl border px-1.5 py-1 transition-colors ${on ? 'border-primary/50 bg-primary/[0.06]' : 'border-transparent hover:bg-muted'}`}>
                <button type="button" onClick={() => selectFromPanel(s.section_key)} aria-current={on ? 'true' : undefined} className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-1.5 py-1.5 text-left">
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-[11px] font-semibold ${on ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>{i + 1}</span>
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate text-sm font-medium ${s.is_enabled ? 'text-foreground' : 'text-muted-foreground line-through'}`}>{sectionLabel(s.section_key)}</span>
                    <span className="block truncate text-[11px] text-muted-foreground">{s.section_key}</span>
                  </span>
                  {pend > 0 && <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">Pendiente</span>}
                </button>
                <div className="flex shrink-0 items-center">
                  <button type="button" disabled={!!busy || i === 0} onClick={() => void move(i, -1)} aria-label={`Subir ${sectionLabel(s.section_key)}`} className="rounded-lg p-1.5 text-muted-foreground hover:bg-card hover:text-foreground disabled:opacity-30"><ChevronUp size={15} /></button>
                  <button type="button" disabled={!!busy || i === sections.length - 1} onClick={() => void move(i, 1)} aria-label={`Bajar ${sectionLabel(s.section_key)}`} className="rounded-lg p-1.5 text-muted-foreground hover:bg-card hover:text-foreground disabled:opacity-30"><ChevronDown size={15} /></button>
                  <button type="button" disabled={!!busy} onClick={() => void toggle(s)} aria-label={s.is_enabled ? `Ocultar ${sectionLabel(s.section_key)}` : `Mostrar ${sectionLabel(s.section_key)}`} aria-pressed={!s.is_enabled} className="rounded-lg p-1.5 text-muted-foreground hover:bg-card hover:text-foreground disabled:opacity-30">
                    {s.is_enabled ? <Eye size={15} /> : <EyeOff size={15} />}
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );

  const editorForm = current && form && (
    <div className="mt-4 border-t border-border pt-4">
      <div className="mb-3 flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold text-foreground">{sectionLabel(current.section_key)}</h2>
          <p className="text-xs text-muted-foreground">Contenido de la sección{pendingByKey.get(current.section_key)?.length ? ' · con cambios sin publicar' : ''}</p>
        </div>
        <button type="button" onClick={() => setSelected(null)} aria-label="Cerrar sección" className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted"><X size={15} /></button>
      </div>
      <FieldsForm value={form} onChange={setForm} />
      <div className="mt-3 rounded-2xl border border-border">
        <button type="button" onClick={() => setJsonOpen((o) => !o)} aria-expanded={jsonOpen} className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-foreground">
          <Braces size={15} className="text-muted-foreground" aria-hidden /> Avanzado (JSON)
          <ChevronDown size={15} aria-hidden className={`ml-auto text-muted-foreground transition-transform ${jsonOpen ? 'rotate-180' : ''}`} />
        </button>
        {jsonOpen && (
          <div className="border-t border-border p-3">
            <label htmlFor="se-json" className="sr-only">Contenido completo en JSON</label>
            <textarea
              id="se-json"
              value={jsonText}
              spellCheck={false}
              rows={10}
              onChange={(e) => {
                setJsonText(e.target.value);
                try {
                  const v = JSON.parse(e.target.value);
                  if (!v || typeof v !== 'object' || Array.isArray(v)) throw new Error('El contenido debe ser un objeto { … }.');
                  setJsonErr(null);
                  setForm(v);
                } catch (err: any) {
                  setJsonErr(err?.message?.startsWith('El contenido') ? err.message : 'JSON incompleto o inválido.');
                }
              }}
              className="w-full rounded-xl border border-border bg-background px-3 py-2 font-mono text-xs text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            {jsonErr && <p className="mt-1 text-xs text-destructive">{jsonErr}</p>}
            <p className="mt-1 text-[11px] text-muted-foreground">Solo texto plano. Enlaces: https://, mailto:, tel:, rutas (/pagina) o anclas (#seccion).</p>
          </div>
        )}
      </div>
      <div className="sticky bottom-0 -mx-4 mt-4 flex gap-2 border-t border-border bg-card/95 px-4 py-3 backdrop-blur">
        <Button variant={primaryVariant} disabled={!dirty || !!busy || !!jsonErr} onClick={() => void saveForm()} className="flex-1">
          {busy === 'save' ? <Loader2 size={15} className="animate-spin" aria-hidden /> : <Check size={15} aria-hidden />}
          {editMode.data === 'apply' && isAdmin ? 'Guardar y aplicar' : 'Guardar como cambio'}
        </Button>
        <Button variant="ghost" disabled={!dirty || !!busy} onClick={() => setForm(clone(current.config))}>Descartar</Button>
      </div>
    </div>
  );

  const editTab = (
    <div className="px-4 pb-4 pt-3">
      {sectionsList}
      {!current && sections.length > 0 && (
        <p className="mt-4 rounded-xl bg-muted px-3 py-2.5 text-xs text-muted-foreground">
          <Pencil size={12} className="mr-1 inline" aria-hidden /> Haz clic en una sección de la vista previa (o en la lista) para editar su contenido.
        </p>
      )}
      {editorForm}
    </div>
  );

  const [agentMounted, setAgentMounted] = useState(false);
  useEffect(() => {
    if (tab === 'agente' || mobileView === 'agente') setAgentMounted(true);
  }, [tab, mobileView]);
  const agentCtx = { page, pageName: pageName(page), selected, onTurnDone: () => void refreshAll(), chatKey };
  const agentTab = (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2">
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          Contexto: <span className="font-medium text-foreground">{pageName(page)}</span>{selected ? <> · <span className="font-medium text-foreground">{sectionLabel(selected)}</span></> : null}
        </span>
        <Button variant="ghost" onClick={() => setChatKey((k) => k + 1)} aria-label="Nuevo chat"><MessageSquarePlus size={15} aria-hidden /></Button>
      </div>
      {/* Se monta al abrir la pestaña por 1.ª vez (no crea hilos si no se usa). */}
      <div className="min-h-0 flex-1">{!agentMounted ? null : renderAgent ? renderAgent(agentCtx) : <p className="p-4 text-sm text-muted-foreground">Chat no disponible.</p>}</div>
    </div>
  );

  const tabs = (value: PanelTab | MobileView, set: (v: any) => void, withPreview: boolean) => (
    <div role="tablist" aria-label="Panel del editor" className="flex gap-1 rounded-xl border border-border bg-card p-1">
      {[
        ...(withPreview ? [{ v: 'preview', label: 'Vista', icon: <Eye size={15} aria-hidden /> }] : []),
        { v: 'editar', label: 'Editar', icon: <Pencil size={15} aria-hidden /> },
        { v: 'agente', label: 'Agente', icon: <Bot size={15} aria-hidden /> },
      ].map((t) => (
        <button
          key={t.v}
          type="button"
          role="tab"
          aria-selected={value === t.v}
          onClick={() => set(t.v)}
          className={`inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-1.5 text-sm font-medium transition-colors ${value === t.v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
        >
          {t.icon} {t.label}
        </button>
      ))}
    </div>
  );

  const noticeEl = notice && (
    <div role={notice.tone === 'err' ? 'alert' : 'status'} className="px-4 pt-3">
      {notice.tone === 'err' ? <ErrorNote>{notice.text}</ErrorNote> : <p className="rounded-lg bg-primary/10 px-3 py-2 text-xs text-primary">{notice.text}</p>}
    </div>
  );

  // ── Diseño ─────────────────────────────────────────────
  if (isMobile) {
    const view = mobileView;
    return (
      <div className="flex w-full min-w-0 flex-col bg-background text-foreground" style={{ height, minHeight: 520 }}>
        {toolbar}
        {pendingBar}
        <div className="px-3 py-2">{tabs(view, setMobileView, true)}</div>
        {noticeEl}
        <div className={view === 'preview' ? 'flex min-h-0 flex-1' : 'hidden'}>{previewStage}</div>
        <div className={view === 'editar' ? 'min-h-0 flex-1 overflow-y-auto' : 'hidden'}>{editTab}</div>
        <div className={view === 'agente' ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}>{agentTab}</div>
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col bg-background text-foreground" style={{ height, minHeight: 560 }}>
      {toolbar}
      {pendingBar}
      <div className="flex min-h-0 flex-1 gap-3 p-3">
        {previewStage}
        <aside
          aria-label="Panel del editor"
          className={`orion-card flex min-h-0 w-[380px] shrink-0 flex-col overflow-hidden rounded-[20px] border border-border bg-card xl:w-[420px] ${panelOpen ? '' : 'hidden'}`}
        >
          <div className="border-b border-border p-2">{tabs(tab, setTab, false)}</div>
          {noticeEl}
          <div className={tab === 'editar' ? 'min-h-0 flex-1 overflow-y-auto' : 'hidden'}>{editTab}</div>
          <div className={tab === 'agente' ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}>{agentTab}</div>
        </aside>
      </div>
    </div>
  );
}
